import { api } from '@lib/http';
import { AddOn, AddOnType } from '@model/AddOn';
import { Price } from '@model/Price';
import { Subcategory, SubcategoryType } from '@model/Subcategory';
import { BuilderModelType } from '@packages/builder/models/BuilderModel';
import { BuilderTextureType } from '@packages/builder/models/BuilderTexture';
import { ImageObject } from '@packages/image/lib/types';
import * as Yup from 'yup';
import { Model, ModelClass } from './Model';

export interface SkuType extends ModelClass {
    created_at: any;
    updated_at: any;
    id: number;
    internal_id: string;
    preview_photo: ImageObject;
    preview_photo_id: number;
    page_title: string;
    meta_keywords: string;
    short_description: string;
    description: string;
    name: string;
    slug: string;
    made_in: number;
    notes: string;
    details: any;
    factory_ids: any;
    factories: any;
    sizing_ranges: [];
    sizing_range_ids: [];
    weight: number;
    category_id: number;
    category: any;
    spec_allow_lowercase: boolean;
    spec_sizeable: boolean;
    spec_nameable: boolean;
    spec_numberable: boolean;
    prices: [];
    factory_prices: [];
    sku_parent_id: number;
    sku_variants: [];
    minimum_order_quantity: number;
    international_minimum_order_quantity: number;
    shipping_cost: string;
    subcategory: SubcategoryType;
    price_range: any;
    subcategory_id: number;
    customer_custom_prices: Price[];
    model_id: number;
    model: BuilderModelType;
    product_id: string;
    id_type: string;
    model_texture_id: number;
    model_texture: BuilderTextureType;
    is_sizing_kit: boolean;
    published: boolean;
    hide_from_builder: boolean;
    sku_media: [];
    sizing_charts: [];
    yotpo_review: string;
    categories_subcategories: SubcategoryType[];
    bind3DModel(texture_id?: number): Promise<null | SkuType>;
    addons: AddOnType[];
    addon_ids: [];
    swatch_ids: [];
    is_shipping_method_available: boolean;
    shipping_method_ids: [];
    vendor_id: number;
    vendor: string;
}

export const Sku = Model<SkuType>('Sku', { endpoint: 'apparel-skus' })`
    id:Int;
    internal_id:String;
    preview_photo:Any;
    preview_photo_id:Int;
    page_title:String;
    meta_keywords:String;
    short_description:String;
    description:String;
    name:String;
    slug:String;
    made_in:Int;
    notes:String;
    details:Any;
    factory_ids:Any;
    factories:Any;
    sizing_ranges:Any;
    sizing_range_ids:Any;
    weight:Float;
    category_id:Int;
    category:Any;
    spec_allow_lowercase:boolean;
    spec_sizeable:boolean;
    spec_nameable:boolean;
    spec_numberable:boolean;
    prices:Any;
    factory_prices:Any;
    sku_parent_id:Int;
    sku_variants:Any;
    minimum_order_quantity:Int;
    international_minimum_order_quantity:Int;
    shipping_cost:String;
    subcategory_id:Int;
    model_id: Any;
    model: Any;
    model_texture_id: Any;
    model_texture: Any;
    is_sizing_kit: boolean;
    published:boolean;
    hide_from_builder:boolean;
    sku_media:Any;
    sizing_charts:Any;
    yotpo_review: String;
    categories_subcategories: ${Subcategory};
    addons: ${AddOn};
    addon_ids:Any;
    swatch_ids:Any;
    is_shipping_method_available:boolean;
    shipping_method_ids:Any;
    vendor_id:Int;
`;

Sku.$define('bind3DModel', async (self, texture_id?: number, swatch_ids?) => {
    const texture = texture_id ?? self.model_texture_id;
    const swatchIds = swatch_ids ?? self.swatch_ids;

    if (!self.id || !self.model_id || !texture) {
        return null;
    }

    return await api.post(
        `apparel-skus/${self.id}/bind-3d-model`,
        {
            model_id: self.model_id,
            texture_id: texture,
            swatch_ids: swatchIds,
        },
        {
            params: {
                fields: ['id', 'model_id', 'model_texture_id'],
            },
        },
    );
});

export const PRICE_RANGE = [
    { minimum: 1, maximum: 9 },
    { minimum: 10, maximum: 25 },
    { minimum: 26, maximum: 50 },
    { minimum: 51, maximum: 100 },
    { minimum: 101, maximum: 250 },
    { minimum: 251, maximum: 500 },
    { minimum: 501, maximum: 1000 },
    { minimum: 1001, maximum: 10000 },
];

export const PRICE_TYPES = [
    { id: 'salesmen_cost', name: 'Manufacturing Cost' },
    { id: 'sponsorship_cost', name: 'Sponsorship Cost' },
    { id: 'shipping_cost', name: 'Per Item Shipping Cost' },
    { id: 'fixed_shipping_cost', name: 'Fixed Shipping Cost *' },
    { id: 'wholesale_price', name: 'Wholesale Price' },
    { id: 'customer_price', name: 'Customer Price' },
];

export const FACTORY_TYPES = [
    { id: 'factory_cost', name: 'Factory Cost' },
    { id: 'factory_shipping_cost', name: 'Factory Shipping' },
    { id: 'tackle_twill', name: 'Tackle Twill' },
    { id: 'embroidry', name: 'Embroidery' },
];

export const SkuValidation = Yup.object({
    internal_id: Yup.string().required('Internal ID is required'),
    name: Yup.string().required('Name is required'),
    subcategories: Yup.array().of(Yup.string()).min(1, 'Subcategory must be selected'),
    page_title: Yup.string().max(100, 'Page title must be at most 100 characters'),
    short_description: Yup.string().max(400, 'Short description must be at most 400 characters'),
});
